// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Inward (Stage 6P.5).
//
// Three-tab Inward control surface at /erp/inventory/inward.
//
//   Tab 1 — Accepted GRNs Pending Posting
//     Source: inventoryApi.getPendingGrns
//   Tab 2 — Approved Purchase Bills Pending Posting
//     Source: inventoryApi.getPendingBills
//   Tab 3 — Posted Inward
//     Source: inventoryApi.getPostedSources
//
// Per-row actions:
//   • Preview Posting       → opens a Drawer that calls
//                             inventoryApi.previewFromGrn /
//                             previewFromPurchaseBill and renders the
//                             normalised candidate list.
//   • Post to Inventory     → confirms via Modal then calls
//                             inventoryApi.postFromGrn /
//                             postFromPurchaseBill, then refreshes the
//                             tab so the row migrates from "pending" to
//                             "posted" without a manual reload.
//   • View Ledger           → navigates to /erp/inventory/stock-ledger
//                             pre-filtered to the source row.
//
// Posting math, duplicate-block decisions, and ledger writes remain
// owned by the backend (inventoryPosting.service.js); this page is a
// pure operator surface.
//
// Layout style: reuses the GRN list shell CSS via the .grn-shell
// wrapper class so the Inward page matches the existing 22 px row
// density, slate-50 header strip, and sticky toolbar that the rest
// of /erp/purchase already ships. NO new CSS file.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, Descriptions, Drawer, Dropdown, Modal,
  Space, Spin, Statistic, Table, Tabs, Tag, Tooltip, message
} from 'antd';
import {
  ReloadOutlined, MoreOutlined, ExclamationCircleOutlined,
  InboxOutlined, EyeOutlined, CheckCircleOutlined,
  FileSearchOutlined, ProfileOutlined
} from '@ant-design/icons';

import { inventoryApi } from './inventoryApi';
import {
  normalizeInventoryPostingPreview,
  summarizePostingPreview,
  postingStatusLabel,
  postingStatusColor,
  itemTypeLabel,
  itemTypeColor,
  isNonStockable
} from './inventoryHelpers';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';


// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = ['25', '50', '100'];

const TAB_KEYS = Object.freeze({
  PENDING_GRN:    'pending-grn',
  PENDING_BILL:   'pending-bill',
  POSTED:         'posted'
});

const SOURCE_TYPE_GRN  = 'GRN';
const SOURCE_TYPE_BILL = 'PURCHASE_BILL';

const ACTION_POST              = 'POST';
const ACTION_SKIP_NON_STOCKABLE = 'SKIP_NON_STOCKABLE';
const ACTION_DUPLICATE_BLOCKED  = 'DUPLICATE_BLOCKED';


// ─────────────────────────────────────────────────────────────────
// Pure helpers
// ─────────────────────────────────────────────────────────────────

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// Source no — GRN rows use grn_no / goods_receipt_number; bills use
// bill_no. Posted rows surface either depending on source_type.
function readSourceNo(row) {
  return (
    pickField(row, 'grn_no',                'grnNo')
    || pickField(row, 'goods_receipt_number','goodsReceiptNumber')
    || pickField(row, 'bill_no',             'billNo')
    || pickField(row, 'source_no',           'sourceNo')
    || ''
  );
}

function readSourceDate(row) {
  return (
    pickField(row, 'grn_date',     'grnDate')
    || pickField(row, 'bill_date', 'billDate')
    || pickField(row, 'source_date', 'sourceDate')
    || null
  );
}

function readSourceType(row) {
  const raw = pickField(row, 'source_type', 'sourceType');
  if (raw) return String(raw).trim().toUpperCase();
  // Heuristic: presence of bill_no / billNo implies a bill row.
  if (pickField(row, 'bill_no', 'billNo')) return SOURCE_TYPE_BILL;
  return SOURCE_TYPE_GRN;
}

function readSupplierName(row) {
  return (
    pickField(row, 'supplier_name', 'supplierName')
    || pickField(row, 'supplier',     'supplier')
    || ''
  );
}

function readSupplierGstin(row) {
  return (
    pickField(row, 'supplier_gstin', 'supplierGstin')
    || pickField(row, 'gstin', 'gstin')
    || ''
  );
}

function readLineCount(row) {
  if (!row || typeof row !== 'object') return 0;
  const candidates = [
    row.line_count, row.lineCount,
    row.lines_count, row.linesCount,
    row.total_lines, row.totalLines,
    row.candidate_count, row.candidateCount
  ];
  for (const c of candidates) {
    if (c !== undefined && c !== null) {
      const n = Number(c);
      if (Number.isFinite(n)) return n;
    }
  }
  if (Array.isArray(row.lines)) return row.lines.length;
  return 0;
}

function readStatus(row) {
  return (
    pickField(row, 'status', 'status')
    || pickField(row, 'header_status', 'headerStatus')
    || ''
  );
}

// Posting status — pending rows surface a "NOT_POSTED" or null; posted
// rows surface POSTED / REVERSED / CANCELLED via postedStatus.
function readPostingStatus(row) {
  return (
    pickField(row, 'posting_status', 'postingStatus')
    || pickField(row, 'posted_status', 'postedStatus')
    || pickField(row, 'inventory_posting_status', 'inventoryPostingStatus')
    || ''
  );
}

function readSourceId(row) {
  return (
    pickField(row, 'id', 'id')
    || pickField(row, 'source_id', 'sourceId')
    || pickField(row, 'grn_id', 'grnId')
    || pickField(row, 'goods_receipt_id', 'goodsReceiptId')
    || pickField(row, 'purchase_bill_id', 'purchaseBillId')
    || pickField(row, 'bill_id', 'billId')
    || ''
  );
}


// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function PurchaseInwardPage() {
  const navigate = useNavigate();
  const { me } = useAuth();

  // ── Permissions ─────────────────────────────────────────────
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canPreview = hasPerm('erp.inventory.inward.preview');
  const canPost    = hasPerm('erp.inventory.inward.post');
  const canLedger  = hasPerm('erp.inventory.stock_ledger.view');
  // Either preview or post is enough to land on the page.
  const canView    = canPreview || canPost;

  // ── Tab state ───────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(TAB_KEYS.PENDING_GRN);

  // Per-tab data state. Independent objects so switching tabs
  // doesn't flash stale rows in.
  const [tabState, setTabState] = useState({
    [TAB_KEYS.PENDING_GRN]:  { rows: [], total: 0, page: 1, pageSize: 25, loading: false },
    [TAB_KEYS.PENDING_BILL]: { rows: [], total: 0, page: 1, pageSize: 25, loading: false },
    [TAB_KEYS.POSTED]:       { rows: [], total: 0, page: 1, pageSize: 25, loading: false }
  });

  const [busyId, setBusyId] = useState(null);

  // ── Preview drawer state ────────────────────────────────────
  const [previewOpen,    setPreviewOpen]    = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRow,     setPreviewRow]     = useState(null);
  const [previewData,    setPreviewData]    = useState(null);
  const [previewError,   setPreviewError]   = useState(null);

  // ── Loaders — one per tab. Each keeps its tab state isolated.
  const loadTab = useCallback(async (tabKey) => {
    if (!canView) return;
    setTabState((prev) => ({
      ...prev,
      [tabKey]: { ...prev[tabKey], loading: true }
    }));
    try {
      const slot = tabState[tabKey];
      const params = { page: slot.page, pageSize: slot.pageSize };

      let out;
      if (tabKey === TAB_KEYS.PENDING_GRN) {
        out = await inventoryApi.getPendingGrns(params);
      } else if (tabKey === TAB_KEYS.PENDING_BILL) {
        out = await inventoryApi.getPendingBills(params);
      } else {
        out = await inventoryApi.getPostedSources(params);
      }

      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : Array.isArray(out?.items) ? out.items
        : [];
      const count = Array.isArray(out) ? list.length
        : Number(out?.total ?? out?.count ?? list.length) || 0;

      setTabState((prev) => ({
        ...prev,
        [tabKey]: { ...prev[tabKey], rows: list, total: count, loading: false }
      }));
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load Inward rows.'),
        key: `inv-inward-${tabKey}`
      });
      setTabState((prev) => ({
        ...prev,
        [tabKey]: { ...prev[tabKey], rows: [], total: 0, loading: false }
      }));
    }
    // tabState read inside is intentional: pagination changes drive
    // a re-call via the dedicated effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  // Reload whichever tab is active when its slice (page/pageSize)
  // changes. We key the effect on the active tab + its slice only;
  // other tabs lazy-load on first switch.
  const activeSlot = tabState[activeTab];
  useEffect(() => {
    loadTab(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeSlot.page, activeSlot.pageSize]);

  const refreshActive = useCallback(() => loadTab(activeTab), [loadTab, activeTab]);

  // ── Preview drawer handlers ─────────────────────────────────
  const openPreview = useCallback(async (row) => {
    const id   = readSourceId(row);
    const type = readSourceType(row);
    if (!id) {
      message.error('Could not resolve source id for preview.');
      return;
    }
    setPreviewRow({ row, id, type });
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    setPreviewError(null);
    try {
      const raw = type === SOURCE_TYPE_BILL
        ? await inventoryApi.previewFromPurchaseBill(id)
        : await inventoryApi.previewFromGrn(id);
      setPreviewData(normalizeInventoryPostingPreview(raw));
    } catch (e) {
      setPreviewError(errMessage(e, 'Failed to load posting preview.'));
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewRow(null);
    setPreviewData(null);
    setPreviewError(null);
  }, []);

  // ── Post handler — confirmed via Modal, then API call ───────
  const runPost = useCallback((row) => {
    const id   = readSourceId(row);
    const type = readSourceType(row);
    if (!id) return;

    const sourceNo = readSourceNo(row) || id;
    const sourceLabel = type === SOURCE_TYPE_BILL ? 'Purchase Bill' : 'GRN';

    Modal.confirm({
      title: `Post ${sourceLabel} to inventory?`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <span style={{ fontSize: 12.5 }}>
          This will create ledger movements for{' '}
          <strong>{sourceNo}</strong>. The backend enforces duplicate
          and stockability rules; SERVICE / JOBWORK lines are skipped.
        </span>
      ),
      okText: 'Post to Inventory',
      cancelText: 'Cancel',
      async onOk() {
        setBusyId(id);
        try {
          if (type === SOURCE_TYPE_BILL) {
            await inventoryApi.postFromPurchaseBill(id);
          } else {
            await inventoryApi.postFromGrn(id);
          }
          message.success(`Posted ${sourceLabel} ${sourceNo} to inventory.`);
          // Refresh active tab + the "Posted" tab so the operator can
          // navigate to it and see the new row without a reload.
          await loadTab(activeTab);
          if (activeTab !== TAB_KEYS.POSTED) {
            await loadTab(TAB_KEYS.POSTED);
          }
        } catch (e) {
          message.error(errMessage(e, 'Failed to post to inventory.'));
          throw e;
        } finally {
          setBusyId(null);
        }
      }
    });
  }, [loadTab, activeTab]);

  // ── View ledger handler — navigates to the stock ledger with
  // the source row pre-filtered. The Stock Ledger page reads
  // sourceType + sourceId from the URL query string.
  const openLedger = useCallback((row) => {
    const id   = readSourceId(row);
    const type = readSourceType(row);
    if (!id) return;
    const qs = new URLSearchParams();
    qs.set('sourceType', type);
    qs.set('sourceId',   String(id));
    navigate(`/erp/inventory/stock-ledger?${qs.toString()}`);
  }, [navigate]);

  // ── Row-action menu ─────────────────────────────────────────
  const buildRowMenu = useCallback((row) => {
    const items = [];
    const posted = String(readPostingStatus(row) || '').trim().toUpperCase() === 'POSTED';

    if (canPreview) {
      items.push({
        key:   'preview',
        icon:  <FileSearchOutlined />,
        label: 'Preview Posting'
      });
    }
    if (canPost && !posted) {
      items.push({
        key:   'post',
        icon:  <CheckCircleOutlined />,
        label: 'Post to Inventory'
      });
    }
    if (canLedger) {
      if (items.length > 0) items.push({ type: 'divider' });
      items.push({
        key:   'ledger',
        icon:  <ProfileOutlined />,
        label: 'View Ledger'
      });
    }
    if (items.length === 0) {
      items.push({
        key:      'noop',
        label:    'No actions available',
        disabled: true
      });
    }
    return items;
  }, [canPreview, canPost, canLedger]);

  const onRowMenu = useCallback((key, row) => {
    switch (key) {
      case 'preview': return openPreview(row);
      case 'post':    return runPost(row);
      case 'ledger':  return openLedger(row);
      default:        return undefined;
    }
  }, [openPreview, runPost, openLedger]);

  // ── Columns ─────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Source No',
      dataIndex: 'sourceNo',
      width:     170,
      render: (_v, row) => {
        const no   = readSourceNo(row);
        const type = readSourceType(row);
        const tagColor = type === SOURCE_TYPE_BILL ? 'geekblue' : 'blue';
        const tagText  = type === SOURCE_TYPE_BILL ? 'Bill' : 'GRN';
        return (
          <Space size={4} align="center">
            <Tag color={tagColor} className="grn-status-tag">{tagText}</Tag>
            <span className="grn-mono">{no || '—'}</span>
          </Space>
        );
      }
    },
    {
      title:     'Source Date',
      dataIndex: 'sourceDate',
      width:     120,
      render: (_v, row) => (
        <span className="grn-mono">{fmtDate(readSourceDate(row))}</span>
      )
    },
    {
      title:     'Supplier',
      dataIndex: 'supplier',
      render: (_v, row) => {
        const name  = readSupplierName(row) || '—';
        const gstin = readSupplierGstin(row);
        return (
          <div className="grn-supplier">
            <Tooltip title={name}>
              <span className="grn-supplier__name">{name}</span>
            </Tooltip>
            {gstin
              ? <Tag color="blue" className="grn-gstin-tag">{gstin}</Tag>
              : null}
          </div>
        );
      }
    },
    {
      title:     'Status',
      dataIndex: 'status',
      width:     130,
      render: (_v, row) => {
        const v = readStatus(row);
        if (!v) return <span className="grn-muted">—</span>;
        return <Tag className="grn-status-tag">{String(v).replace(/_/g, ' ')}</Tag>;
      }
    },
    {
      title:     'Lines',
      dataIndex: 'lineCount',
      width:     80,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">{readLineCount(row)}</span>
      )
    },
    {
      title:     'Posting Status',
      dataIndex: 'postingStatus',
      width:     150,
      render: (_v, row) => {
        const v = readPostingStatus(row);
        if (!v) return (
          <Tag className="grn-status-tag" color="default">Not Posted</Tag>
        );
        return (
          <Tag color={postingStatusColor(v)} className="grn-status-tag">
            {postingStatusLabel(v) || String(v).replace(/_/g, ' ')}
          </Tag>
        );
      }
    },
    {
      title:     '',
      key:       '_actions',
      width:     56,
      fixed:     'right',
      align:     'center',
      render: (_v, row) => {
        const items = buildRowMenu(row);
        const id = readSourceId(row);
        return (
          <Dropdown
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                if (domEvent && domEvent.stopPropagation) domEvent.stopPropagation();
                onRowMenu(key, row);
              }
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              className="grn-actions-btn"
              icon={<MoreOutlined />}
              loading={busyId === id}
              onClick={(e) => e.stopPropagation()}
              aria-label="Row actions"
            />
          </Dropdown>
        );
      }
    }
  ]), [buildRowMenu, onRowMenu, busyId]);

  // ── Per-tab Table ───────────────────────────────────────────
  const renderTable = useCallback((tabKey, emptyText) => {
    const slot = tabState[tabKey];
    return (
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={slot.loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) => readSourceId(r) || readSourceNo(r)}
            dataSource={slot.rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            onRow={(row) => ({
              onDoubleClick: () => openPreview(row)
            })}
            pagination={{
              current:           slot.page,
              pageSize:          slot.pageSize,
              total:             slot.total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   slot.total > slot.pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                setTabState((prev) => {
                  const cur = prev[tabKey];
                  if (nextSize !== cur.pageSize) {
                    return {
                      ...prev,
                      [tabKey]: { ...cur, page: 1, pageSize: nextSize }
                    };
                  }
                  return {
                    ...prev,
                    [tabKey]: { ...cur, page: nextPage }
                  };
                });
              }
            }}
            locale={{
              emptyText: slot.loading ? ' ' : emptyText
            }}
          />
        </Spin>
      </Card>
    );
  }, [tabState, columns, openPreview]);

  // ── Render ──────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view inventory inward."
          description="Ask your master account to grant erp.inventory.inward.preview or erp.inventory.inward.post."
        />
      </div>
    );
  }

  const slot = tabState[activeTab];

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><InboxOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">Purchase Inward</span>
            <span className="grn-headstrip__subtitle">
              Post accepted GRNs and approved Purchase Bills into the
              stock ledger. Backend enforces duplicate, stockability,
              and ledger math; this surface previews + dispatches.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{slot.total}</strong> in tab
          </span>
          <Tooltip title="Refresh">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={refreshActive}
            />
          </Tooltip>
        </Space>
      </div>

      <Tabs
        size="small"
        activeKey={activeTab}
        onChange={(k) => setActiveTab(k)}
        items={[
          {
            key:      TAB_KEYS.PENDING_GRN,
            label:    'Accepted GRNs Pending Posting',
            children: renderTable(
              TAB_KEYS.PENDING_GRN,
              'No accepted GRNs are waiting to be posted.'
            )
          },
          {
            key:      TAB_KEYS.PENDING_BILL,
            label:    'Approved Purchase Bills Pending Posting',
            children: renderTable(
              TAB_KEYS.PENDING_BILL,
              'No approved purchase bills are waiting to be posted.'
            )
          },
          {
            key:      TAB_KEYS.POSTED,
            label:    'Posted Inward',
            children: renderTable(
              TAB_KEYS.POSTED,
              'No inventory inward has been posted yet.'
            )
          }
        ]}
      />

      {/* Preview drawer */}
      <Drawer
        title={
          previewRow
            ? `Posting preview — ${
                previewRow.type === SOURCE_TYPE_BILL ? 'Purchase Bill' : 'GRN'
              } ${readSourceNo(previewRow.row) || ''}`.trim()
            : 'Posting preview'
        }
        placement="right"
        width={780}
        open={previewOpen}
        onClose={closePreview}
        destroyOnClose
        extra={
          previewRow && canPost
          && String(readPostingStatus(previewRow.row) || '').toUpperCase() !== 'POSTED'
            ? (
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  closePreview();
                  runPost(previewRow.row);
                }}
                disabled={
                  !previewData
                  || (previewData.postableCount || 0) === 0
                }
              >
                Post to Inventory
              </Button>
            )
            : null
        }
      >
        {previewLoading ? (
          <Spin spinning>
            <div style={{ minHeight: 200 }} />
          </Spin>
        ) : previewError ? (
          <Alert type="error" showIcon message={previewError} />
        ) : previewData ? (
          <PreviewBody data={previewData} />
        ) : (
          <Alert type="info" showIcon message="No preview data available." />
        )}
      </Drawer>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Preview drawer body — renders the candidate list + a summary card.
// Pure presentation; never mutates the incoming data shape.
// ─────────────────────────────────────────────────────────────────

function PreviewBody({ data }) {
  const summary = summarizePostingPreview(data);
  const header  = data.sourceHeader || {};

  const candidateColumns = [
    {
      title:     '#',
      dataIndex: '_idx',
      width:     40,
      render: (_v, _row, idx) => <span className="grn-mono">{idx + 1}</span>
    },
    {
      title:     'Item',
      dataIndex: 'itemName',
      render: (_v, c) => (
        <Space size={6} direction="vertical" style={{ lineHeight: 1.2 }}>
          <span style={{ fontSize: 12.5, fontWeight: 500 }}>
            {c.itemName || '—'}
          </span>
          {c.itemType ? (
            <Tag color={itemTypeColor(c.itemType)} className="grn-status-tag">
              {itemTypeLabel(c.itemType)}
            </Tag>
          ) : null}
        </Space>
      )
    },
    {
      title:     'Qty',
      dataIndex: 'qty',
      width:     80,
      align:     'right',
      render: (v, c) => (
        <span className="grn-mono">
          {Number(v || 0).toFixed(2)} {c.uom || ''}
        </span>
      )
    },
    {
      title:     'Sqmt / Sqft',
      dataIndex: 'sqmt',
      width:     130,
      align:     'right',
      render: (_v, c) => {
        if (!c.sqmt && !c.sqft) return <span className="grn-muted">—</span>;
        return (
          <span className="grn-mono">
            {Number(c.sqmt || 0).toFixed(4)} / {Number(c.sqft || 0).toFixed(4)}
          </span>
        );
      }
    },
    {
      title:     'Length',
      dataIndex: 'totalLength',
      width:     100,
      align:     'right',
      render: (v) => {
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{Number(v).toFixed(4)}</span>;
      }
    },
    {
      title:     'Action',
      dataIndex: 'action',
      width:     180,
      render: (v, c) => {
        if (v === ACTION_POST) {
          return <Tag color="success" className="grn-status-tag">Will Post</Tag>;
        }
        if (v === ACTION_SKIP_NON_STOCKABLE) {
          return (
            <Tag color="default" className="grn-status-tag">
              Skip (non-stockable)
            </Tag>
          );
        }
        if (v === ACTION_DUPLICATE_BLOCKED) {
          return (
            <Tag color="warning" className="grn-status-tag">
              Duplicate Blocked
            </Tag>
          );
        }
        if (isNonStockable(c.itemType)) {
          return (
            <Tag color="default" className="grn-status-tag">
              Skip (non-stockable)
            </Tag>
          );
        }
        return <Tag className="grn-status-tag">{v || '—'}</Tag>;
      }
    }
  ];

  return (
    <div>
      {/* Summary statistics card */}
      <Card size="small" style={{ marginBottom: 12 }} bordered>
        <Space size={32} wrap>
          <Statistic title="Total Lines" value={summary.total} />
          <Statistic
            title="Postable"
            value={summary.postable}
            valueStyle={{ color: '#16a34a' }}
          />
          <Statistic
            title="Skipped"
            value={summary.skipped}
            valueStyle={{ color: '#64748b' }}
          />
          <Statistic
            title="Duplicates Blocked"
            value={summary.duplicates}
            valueStyle={{ color: '#d97706' }}
          />
        </Space>
      </Card>

      {/* Source header descriptions, if the API supplied any. */}
      {Object.keys(header).length > 0 ? (
        <Card
          size="small"
          title="Source"
          style={{ marginBottom: 12 }}
          bordered
        >
          <Descriptions
            size="small"
            column={2}
            colon
            labelStyle={{ fontSize: 12, color: '#64748b' }}
            contentStyle={{ fontSize: 12.5 }}
          >
            {header.sourceNo ? (
              <Descriptions.Item label="Source No">
                {header.sourceNo}
              </Descriptions.Item>
            ) : null}
            {header.sourceDate ? (
              <Descriptions.Item label="Source Date">
                {fmtDate(header.sourceDate)}
              </Descriptions.Item>
            ) : null}
            {header.supplierName ? (
              <Descriptions.Item label="Supplier">
                {header.supplierName}
              </Descriptions.Item>
            ) : null}
            {header.status ? (
              <Descriptions.Item label="Header Status">
                {String(header.status).replace(/_/g, ' ')}
              </Descriptions.Item>
            ) : null}
          </Descriptions>
        </Card>
      ) : null}

      {/* Candidate lines table */}
      <Table
        size="small"
        rowKey={(c, idx) =>
          c.sourceLineId || c.itemId ? `${c.sourceLineId || ''}::${c.itemId || ''}` : `row-${idx}`}
        dataSource={Array.isArray(data.candidates) ? data.candidates : []}
        columns={candidateColumns}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No candidate lines.' }}
      />

      {summary.postable === 0 ? (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message="Nothing to post"
          description="All candidate lines were either already posted, blocked as duplicates, or skipped as non-stockable."
        />
      ) : null}
    </div>
  );
}
